import {
  animate,
  state,
  style,
  transition,
  trigger
} from "@angular/animations";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  combineLatest as observableCombineLatest,
  fromEvent as observableFromEvent
} from "rxjs";
import { filter, map, startWith } from "rxjs/operators";

import { SidebarSection } from "./types";

const ANIMATION_TIME_MS = 200;

@Component({
  selector: "ta-sidebar",
  templateUrl: "./sidebar.template.html",
  animations: [
    trigger("growInOut", [
      state("void", style({ opacity: 0, transform: "scale(0.1)" })),
      state("*", style({ opacity: 1, transform: "scale(1)" })),
      transition("void <=> *", animate(300))
    ]),
    trigger("growHover", [
      state("*", style({ transform: "scale(1)" })),
      state("true", style({ transform: "scale(1.5)" })),
      transition("true <=> *", animate(150))
    ]),
    trigger("slideInOut", [
      state("in", style({ transform: "translateX(0%)" })),
      state("goLeft", style({ transform: "translateX(0%)" })),
      state("goRight", style({ transform: "translateX(0%)" })),
      transition("void => goRight", [
        style({ transform: "translateX(-100%)" }),
        animate(ANIMATION_TIME_MS)
      ]),
      transition("void => goLeft", [
        style({ transform: "translateX(100%)" }),
        animate(ANIMATION_TIME_MS)
      ]),
      transition("goLeft => void", [
        animate(ANIMATION_TIME_MS, style({ transform: "translateX(-100%)" }))
      ]),
      transition("goRight => void", [
        animate(ANIMATION_TIME_MS, style({ transform: "translateX(100%)" }))
      ]),
      transition("void => in", [])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent {
  hover: boolean = false;

  slideDirection: "goLeft" | "goRight" | "in" = "in";

  @Input()
  set fullHeight(input: boolean) {
    this.fullHeightEmitter.next(input);
  }
  fullHeightEmitter = new BehaviorSubject(true);

  @Input()
  sections: SidebarSection[] = [];

  @Input()
  currentSubsectionId: string;

  @Output()
  currentSubsectionChange: EventEmitter<string> = new EventEmitter();

  @ViewChild("sidebarList", { static: true })
  list: ElementRef;

  height$: Observable<string | null> = observableCombineLatest([
    observableFromEvent(window, "scroll").pipe(startWith(null)),
    this.fullHeightEmitter
  ]).pipe(
    filter(() => window.scrollY < 92),
    map(([_, forceFullHeight]) => {
      if (!forceFullHeight) {
        return null;
      }
      let { top } = this.list.nativeElement.getBoundingClientRect();

      if (top < 0) {
        top = 0;
      }

      return `calc(100vh - ${top}px - 17rem)`;
    }),
    startWith(
      this.fullHeightEmitter.value ? "calc(100vh - 96px - 17rem)" : null
    )
  );
}
